import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AnimatedSection from './AnimatedSection';

const AdminLogin: React.FC = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Where the guard bounced them from, so they land back on it after signing in. */
  const from = (location.state as { from?: string } | null)?.from || '/admin';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }

    try {
      setSubmitting(true);
      await signIn(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in');
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500';

  return (
    <section className="min-h-screen bg-surface flex items-center justify-center py-20 px-4">
      <AnimatedSection animation="slide-up" className="w-full max-w-md">
        <div className="rounded-2xl border border-surface-border bg-surface-card p-8">
          <img src="/nexalogo.png" alt="Nexasolutra" className="w-11 h-11 mb-6 object-contain" />

          <h1 className="font-display text-2xl font-semibold text-white mb-2">Admin sign in</h1>
          <p className="text-muted text-sm mb-8">
            This area is for Nexasolutra staff.
          </p>

          {error && (
            <div className="mb-5 rounded-lg border border-red-500/40 bg-red-500/10 text-red-400 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-muted mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@nexasolutra.com"
                autoComplete="username"
                autoFocus
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm text-muted mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className={inputClass}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </AnimatedSection>
    </section>
  );
};

export default AdminLogin;
